<template>
  <div id="page-consultRegister">
    <div class="container">
      <div class="header">
        <div class="step-indicator">{{ currentStep }} / {{ totalSteps }}</div>
      </div>

      <swiper
        :modules="modules"
        :slides-per-view="1"
        :space-between="0"
        :allow-touch-move="false"
        @swiper="onSwiper"
        class="register-swiper"
      >
        <!-- 1. 이름 -->
        <swiper-slide>
          <div class="step-content-single">
            <div class="icon">👤</div>
            <h1>성함을 입력해주세요</h1>
            <input 
              type="text" 
              v-model="formData.name" 
              placeholder="예) 홍길동"
              @keyup.enter="nextStep"
              @keydown.tab.prevent="nextStep"
              class="main-input"
              autofocus
            >
            <p class="desc">상담을 위해 필요한 정보입니다</p>
          </div>
        </swiper-slide>

        <!-- 2. 전화번호 -->
        <swiper-slide>
          <div class="step-content-single">
            <div class="icon">📞</div>
            <h1>연락처</h1>
            <input 
              type="tel" 
              v-model="formData.phone" 
              placeholder="-없이 번호만 입력해주세요"
              @keyup.enter="nextStep"
              @keydown.tab.prevent="nextStep"
              @input="formatPhone"
              class="main-input"
            >
            <p class="desc">상담 연락을 위해 필요합니다</p>
          </div>
        </swiper-slide>

        <!-- 3. 상담 유형 -->
        <swiper-slide>
          <div class="step-content-single">
            <div class="icon">💼</div>
            <h1>어떤 상담이 필요하신가요?</h1>
            
            <div class="select-grid">
              <button 
                v-for="type in consultTypes" 
                :key="type.value"
                @click="selectConsultType(type.value)"
                class="select-btn"
                :class="{ active: formData.consult_type === type.value }"
              >
                <span class="select-icon">{{ type.icon }}</span>
                <span class="select-label">{{ type.label }}</span>
              </button>
            </div>

            <p class="desc">해당하는 항목을 선택해주세요</p>
          </div>
        </swiper-slide>

        <!-- 4. 메모 -->
        <swiper-slide>
          <div class="step-content-single">
            <div class="icon">📝</div>
            <h1>추가로 전달할 내용이 있나요?</h1>
            <textarea 
              v-model="formData.memo" 
              placeholder="궁금한 점이나 요청사항을 자유롭게 적어주세요 (선택)"
              @keydown.tab.prevent="nextStep"
              class="main-textarea"
              rows="4"
            ></textarea>
            <p class="desc">선택사항이에요</p>
          </div>
        </swiper-slide>

        <!-- 5. 완료 -->
        <swiper-slide>
          <div class="step-content-single completion">
            <div class="icon success">
              <i class="ph-fill ph-check"></i>
            </div>
            <h1>상담 신청 완료!</h1>
            <p class="desc-large">
              소중한 정보 남겨주셔서 감사합니다! 🎉<br>
              빠른 시일 내에 연락드리겠습니다.
            </p>

            <div class="summary">
              <div class="summary-item" v-if="formData.name">
                <i class="ph-fill ph-user"></i>
                <span>{{ formData.name }}</span>
              </div>
              <div class="summary-item" v-if="formData.phone">
                <i class="ph-fill ph-phone"></i>
                <span>{{ formatDisplayPhone(formData.phone) }}</span>
              </div>
              <div class="summary-item" v-if="formData.consult_type">
                <i class="ph-fill ph-clipboard-text"></i>
                <span>{{ getConsultTypeLabel(formData.consult_type) }}</span>
              </div>
              <div class="summary-item" v-if="formData.memo">
                <i class="ph-fill ph-note"></i>
                <span class="memo-preview">{{ formData.memo }}</span>
              </div>
            </div>

            <button @click="finish" class="action-btn primary">
              <i class="ph-fill ph-check"></i>
              확인
            </button>
          </div>
        </swiper-slide>
      </swiper>

      <div class="nav-buttons" v-if="currentStep < totalSteps">
        <button 
          v-if="currentStep > 1" 
          @click="prevStep" 
          class="nav-btn prev"
        >
          <i class="ph ph-arrow-left"></i>
        </button>
        
        <button 
          @click="handleNext" 
          class="nav-btn next"
          :class="{ primary: canProceed, disabled: !canProceed }"
          :disabled="!canProceed"
        >
          <i class="ph ph-arrow-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { inject, ref, computed } from "vue";
import { Swiper, SwiperSlide } from 'swiper/vue';

export default {
  components: {
    Swiper,
    SwiperSlide,
  },
  setup() {
    const sql_query = inject('sql_query');
    
    const swiperInstance = ref(null);
    const currentStep = ref(1);
    const totalSteps = ref(5);
    const modules = [];

    const formData = ref({
      name: '',
      phone: '',
      grade: '',
      consult_type: '',
      memo: '',
    });

    const consultTypes = [
      { value: '신규가입', label: '신규 가입', icon: '✨' },
      { value: '보장분석', label: '보장 분석', icon: '🔍' },
      { value: '보험리모델링', label: '보험 리모델링', icon: '🔄' },
      { value: '보험금청구', label: '보험금 청구', icon: '💰' },
      { value: '자동차보험', label: '자동차 보험', icon: '🚗' },
      { value: '기타문의', label: '기타 문의', icon: '💬' },
    ];

    const canProceed = computed(() => {
      if (currentStep.value === 1) {
        return formData.value.name.trim().length >= 2;
      }
      if (currentStep.value === 2) {
        return formData.value.phone.length >= 10;
      }
      return true;
    });

    function formatPhone() {
      let value = formData.value.phone.replace(/[^0-9]/g, '');
      if (value.length > 11) value = value.slice(0, 11);
      formData.value.phone = value;
    }

    function formatDisplayPhone(phone) {
      if (phone.length === 11) {
        return `${phone.slice(0, 3)}-${phone.slice(3, 7)}-${phone.slice(7)}`;
      } else if (phone.length === 10) {
        return `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}`;
      }
      return phone;
    }

    function selectConsultType(type) {
      formData.value.consult_type = type;
      setTimeout(() => nextStep(), 300);
    }

    function getConsultTypeLabel(value) {
      const found = consultTypes.find(t => t.value === value);
      return found ? found.label : value;
    }

    function onSwiper(swiper) {
      swiperInstance.value = swiper;
    }

    function handleNext() {
      if (!canProceed.value) return;
      
      if (currentStep.value === 4) {
        saveConsult();
      }
      nextStep();
    }

    let doingSwipeNext = false;
    async function nextStep() {
      if (currentStep.value < totalSteps.value && !doingSwipeNext) {
        currentStep.value++;
        await swiperInstance.value?.slideNext();
        doingSwipeNext = true;
        setTimeout(() => {
          if (currentStep.value === 2) {
            const phoneInput = document.querySelector('input[type="tel"]');
            phoneInput?.focus();
          } else if (currentStep.value === 4) {
            const textarea = document.querySelector('textarea');
            textarea?.focus();
          }
        }, 500);
        doingSwipeNext = false;
      }
    }

    function prevStep() {
      if (currentStep.value > 1) {
        currentStep.value--;
        swiperInstance.value?.slidePrev();
      }
    }

    function escapeStr(value) {
      return value !== null && value !== undefined ? String(value).replace(/'/g, "\\'") : null;
    }

    function sanitizeName(name) {
      if (!name) return '무명';

      const cleaned = name
        .replace(/\s+/g, ' ')
        .replace(/[^\p{L}\p{N} ]/gu, '')
        .trim();

      if (cleaned.length < 2) return '무명';
      if (cleaned.length > 10) return cleaned.slice(0, 10);

      return cleaned;
    }

    async function saveConsult() {
      formData.value.name = sanitizeName(formData.value.name);

      const f = formData.value;

      const sql = `INSERT INTO consult_waiting SET
        name = '${escapeStr(f.name)}',
        phone = ${f.phone ? `'${escapeStr(f.phone)}'` : 'NULL'},
        grade = ${f.grade ? `'${escapeStr(f.grade)}'` : 'NULL'},
        consult_type = ${f.consult_type ? `'${escapeStr(f.consult_type)}'` : 'NULL'},
        memo = ${f.memo ? `'${escapeStr(f.memo)}'` : 'NULL'},
        status = 0`;

      await sql_query(sql);
    }

    function finish() {
      alert('상담 신청이 완료되었습니다. 감사합니다!');
      window.close();
    }

    return {
      swiperInstance,
      currentStep,
      totalSteps,
      modules,
      formData,
      consultTypes,
      canProceed,
      onSwiper,
      handleNext,
      nextStep,
      prevStep,
      formatPhone,
      formatDisplayPhone,
      selectConsultType,
      getConsultTypeLabel,
      finish,
    };
  }
}
</script>

<style scoped>
#page-consultRegister {
  width: 100%;
  min-height: 100vh;
  background: var(--color-surface);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  box-sizing: border-box;
}

.container {
  position: relative;
  width: 100%;
  max-width: 500px;
  height: 85vh;
  max-height: 800px;
  background: var(--color-background);
  border-radius: var(--radius-lg);
  padding: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-md);
  overflow: hidden;
}

.header {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px 28px;
  border-bottom: 1px solid var(--color-background-light);
}

.step-indicator {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-brand);
  background: var(--color-brand-light);
  padding: 8px 16px;
  border-radius: var(--radius-lg);
}

.register-swiper {
  flex: 1;
  width: 100%;
  overflow: hidden;
}

.step-content-single {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px 32px;
  box-sizing: border-box;
  gap: 24px;
  text-align: center;
}

.icon {
  font-size: var(--font-size-5xl);
  animation: bounceIn 0.6s ease-out;
  filter: drop-shadow(var(--shadow-sm));
}

@keyframes bounceIn {
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.icon.success {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, var(--color-special-point), #45b049);
  color: var(--color-text-white);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: var(--font-size-4xl);
  box-shadow: var(--shadow-md);
}

h1 {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-extrabold);
  color: var(--color-text-primary);
  margin: 0;
  letter-spacing: -0.8px;
  line-height: 1.3;
}

.desc {
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
  margin: -12px 0 0 0;
  line-height: 1.5;
}

.desc-large {
  font-size: var(--font-size-lg);
  color: var(--color-text-primary);
  margin: -12px 0 0 0;
  line-height: 1.6;
}

.main-input {
  width: 100%;
  padding: 18px 20px;
  box-sizing: border-box;
  border: 2px solid var(--color-background-light);
  border-radius: var(--radius-md);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  background: var(--color-background);
  color: var(--color-text-primary);
  transition: all 0.3s;
  text-align: center;
}

.main-input:focus {
  outline: none;
  border-color: var(--color-brand);
  box-shadow: 0 0 0 4px var(--color-brand-light);
  transform: scale(1.02);
}

.main-input::placeholder {
  color: var(--color-text-secondary);
  opacity: 0.6;
  font-weight: var(--font-weight-medium);
}

.main-textarea {
  width: 100%;
  padding: 18px 20px;
  box-sizing: border-box;
  border: 2px solid var(--color-background-light);
  border-radius: var(--radius-md);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  background: var(--color-background);
  color: var(--color-text-primary);
  transition: all 0.3s;
  resize: none;
  font-family: inherit;
  line-height: 1.6;
}

.main-textarea:focus {
  outline: none;
  border-color: var(--color-brand);
  box-shadow: 0 0 0 4px var(--color-brand-light);
}

.main-textarea::placeholder {
  color: var(--color-text-secondary);
  opacity: 0.6;
}

/* 상담 유형 선택 그리드 */
.select-grid {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.select-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px 16px;
  background: var(--color-background-light);
  border: 2px solid transparent;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.2s;
}

.select-btn:hover {
  background: var(--color-surface);
  transform: translateY(-2px);
}

.select-btn.active {
  background: var(--color-brand-light);
  border-color: var(--color-brand);
}

.select-icon {
  font-size: var(--font-size-2xl);
}

.select-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.select-btn.active .select-label {
  color: var(--color-brand);
}

/* 완료 화면 */
.step-content-single.completion {
  gap: 20px;
}

.summary {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  background: var(--color-background-light);
  border-radius: var(--radius-lg);
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid var(--color-surface-reverse);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
}

.summary-item:last-child {
  border-bottom: none;
}

.summary-item i {
  font-size: var(--font-size-xl);
  color: var(--color-brand);
  flex-shrink: 0;
}

.memo-preview {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 250px;
}

.action-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px;
  border: none;
  border-radius: var(--radius-lg);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 12px;
}

.action-btn.primary {
  background: var(--color-brand);
  color: var(--color-text-black);
  box-shadow: var(--shadow-md);
}

.action-btn.primary:hover {
  opacity: 0.9;
  transform: translateY(-2px);
}

/* 네비게이션 버튼 */
.nav-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 20px 28px;
  border-top: 1px solid var(--color-background-light);
}

.nav-btn {
  width: 56px;
  height: 56px;
  border: none;
  border-radius: 50%;
  background: var(--color-background-light);
  color: var(--color-text-primary);
  font-size: var(--font-size-xl);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: var(--shadow-sm);
}

.nav-btn:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-md);
}

.nav-btn.primary {
  background: var(--color-brand);
  color: var(--color-text-black);
  box-shadow: var(--shadow-md);
}

.nav-btn.disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.nav-btn.disabled:hover {
  transform: none;
}

@media (max-width: 640px) {
  #page-consultRegister {
    padding: 0;
  }

  .container {
    width: 100%;
    height: 100vh;
    border-radius: 0;
    max-height: none;
  }

  .step-content-single {
    padding: 32px 24px;
  }

  h1 {
    font-size: var(--font-size-xl);
  }

  .icon {
    font-size: var(--font-size-4xl);
  }

  .select-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .select-btn {
    padding: 16px 12px;
  }
}
</style>